import { useId, useRef, useState, type ReactNode } from 'react';

/**
 * A tooltip (ARCHITECTURE.md §65).
 *
 * Deliberately NOT the `title` attribute. `title` is invisible to touch users,
 * appears after an uncontrollable delay, cannot be styled, and several screen
 * readers ignore it — so it looks like help while providing none.
 *
 * This shows on hover AND on keyboard focus, because an operator tabbing
 * through a dense console is exactly the person who needs the explanation.
 *
 * The tip is wired with `aria-describedby` rather than `aria-label`: a label
 * would REPLACE the control's own name, so a button reading "Connect" with a
 * tooltip would be announced only as the tooltip text. Describedby adds to the
 * name instead of overwriting it.
 */

export type TooltipProps = {
  /** The explanation. Kept short; anything longer belongs in a help dialog. */
  tip: string;
  children: ReactNode;
  placement?: 'top' | 'bottom';
};

export function Tooltip({ tip, children, placement = 'top' }: TooltipProps): React.JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLSpanElement>(null);

  return (
    <span
      className="tooltip"
      ref={wrapper}
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
      // Focus and blur bubble from the wrapped control, so a keyboard user
      // tabbing onto it gets the same help a mouse user gets on hover.
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={() => {
        setOpen(false);
      }}
      onKeyDown={(event) => {
        // Escape dismisses without moving focus — a tooltip covering the next
        // control is worse than no tooltip.
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <span aria-describedby={id}>{children}</span>

      {/*
       * `role="tooltip"` with the text always in the DOM, hidden by `hidden`
       * rather than unmounted. Assistive technology resolves aria-describedby
       * against the accessibility tree, and a node that does not exist cannot
       * be described.
       */}
      <span
        className={`tooltip__bubble tooltip__bubble--${placement}`}
        id={id}
        role="tooltip"
        hidden={!open}
      >
        {tip}
      </span>
    </span>
  );
}

/**
 * A help affordance that carries its own tooltip.
 *
 * Used beside a field or heading where the explanation is a sentence rather
 * than a paragraph. It is a real `button` so it is tabbable and announced as
 * one; a bare icon with a hover handler would be invisible to keyboard and
 * screen-reader users alike.
 */
export function HelpHint({ tip, label }: { tip: string; label: string }): React.JSX.Element {
  return (
    <Tooltip tip={tip}>
      <button type="button" className="help-hint" aria-label={`Help: ${label}`}>
        {/*
         * An inline SVG, not an emoji. Emoji render differently per platform,
         * are announced unpredictably, and cannot inherit currentColor.
         */}
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.6.2-1 .8-1 1.4v.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
        </svg>
      </button>
    </Tooltip>
  );
}
